import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ArrowRight, Bot, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PropBot } from '@/types/prop-bots';
import { ReassignmentOptions } from '@/services/bot-account-reassignment-service';

interface ReassignAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string | null;
  currentBot: PropBot;
  availableBots: PropBot[];
  isLoadingBots: boolean;
  isReassigning: boolean;
  isAdmin: boolean;
  onReassign: (targetBotId: string, options: ReassignmentOptions) => Promise<void>;
}

export const ReassignAccountDialog: React.FC<ReassignAccountDialogProps> = ({
  isOpen,
  onClose,
  accountId,
  currentBot,
  availableBots,
  isLoadingBots,
  isReassigning,
  isAdmin,
  onReassign
}) => {
  const [selectedBotId, setSelectedBotId] = useState<string>(currentBot.id);
  const [isReversePosition, setIsReversePosition] = useState(false);
  const [keepOnCurrentPage, setKeepOnCurrentPage] = useState(false);
  const [disconnectDuringChange, setDisconnectDuringChange] = useState(true);
  const [updateVolumeMultiplier, setUpdateVolumeMultiplier] = useState(false);
  const [newVolumeMultiplier, setNewVolumeMultiplier] = useState<string>('1.0');

  // Include current bot in selection options
  const allBots = [currentBot, ...availableBots.filter(bot => bot.id !== currentBot.id)];
  const selectedBot = allBots.find(bot => bot.id === selectedBotId);

  const handleReassign = async () => {
    if (!selectedBotId) {
      return;
    }

    const options: ReassignmentOptions = {
      isReversePosition,
      keepOnCurrentPage,
      disconnectDuringChange,
      volumeMultiplier: updateVolumeMultiplier ? parseFloat(newVolumeMultiplier) : undefined
    };

    await onReassign(selectedBotId, options);
  };

  const resetForm = () => {
    setSelectedBotId(currentBot.id);
    setIsReversePosition(false);
    setKeepOnCurrentPage(false);
    setDisconnectDuringChange(true);
    setUpdateVolumeMultiplier(false);
    setNewVolumeMultiplier('1.0');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Reassign Trading Account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Bot Info */}
          <Card className="border-muted">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Bot className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Current Bot</p>
                  <p className="text-lg font-semibold">{currentBot.name}</p>
                </div>
                <Badge variant="outline" className="ml-auto">
                  {currentBot.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Quick Switch for Current Bot */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Transform Current Bot</Label>
            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bot className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium">{currentBot.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Switch between Original and Shadow mode
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={selectedBotId === currentBot.id ? "default" : "secondary"}>
                      Selected
                    </Badge>
                    <Badge variant={isReversePosition ? "destructive" : "default"}>
                      {isReversePosition ? 'Shadow' : 'Original'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Target Bot Selection */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Or Select Different Bot</Label>
            <ScrollArea className="h-48 border rounded-md">
              {isLoadingBots ? (
                <div className="p-3 space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                        <Skeleton className="h-6 w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-2">
                  {allBots.map((bot) => (
                    <div
                      key={bot.id}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedBotId === bot.id
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:border-muted'
                      }`}
                      onClick={() => setSelectedBotId(bot.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {bot.name}
                            {bot.id === currentBot.id && (
                              <span className="ml-2 text-xs text-blue-600 font-semibold">
                                (Current)
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground truncate max-w-[300px]">
                            {bot.description}
                          </p>
                        </div>
                        <Badge 
                          variant={selectedBotId === bot.id ? "default" : "secondary"}
                        >
                          {bot.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Reassignment Options</Label>
            
            <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="reverse-position">Reverse Positions</Label>
                  <p className="text-sm text-muted-foreground">
                    Use shadow bot for opposite signal direction
                  </p>
                </div>
                <Switch
                  id="reverse-position"
                  checked={isReversePosition}
                  onCheckedChange={setIsReversePosition}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="keep-current">Keep on Current Page</Label>
                  <p className="text-sm text-muted-foreground">
                    Account remains visible on current bot's page
                  </p>
                </div>
                <Switch
                  id="keep-current"
                  checked={keepOnCurrentPage}
                  onCheckedChange={setKeepOnCurrentPage}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="disconnect-during">Safe Disconnect</Label>
                  <p className="text-sm text-muted-foreground">
                    Temporarily disconnect during reassignment
                  </p>
                </div>
                <Switch
                  id="disconnect-during"
                  checked={disconnectDuringChange}
                  onCheckedChange={setDisconnectDuringChange}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="update-volume">Update Volume Multiplier</Label>
                  <p className="text-sm text-muted-foreground">
                    Set new volume multiplier during reassignment
                  </p>
                </div>
                <Switch
                  id="update-volume"
                  checked={updateVolumeMultiplier}
                  onCheckedChange={setUpdateVolumeMultiplier}
                />
              </div>

              {updateVolumeMultiplier && (
                <div className="space-y-2 ml-4 p-3 border-l-2 border-primary bg-muted/50 rounded-r-md">
                  <Label htmlFor="volume-multiplier" className="text-sm font-medium">
                    New Volume Multiplier
                  </Label>
                  <Input
                    id="volume-multiplier"
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={newVolumeMultiplier}
                    onChange={(e) => setNewVolumeMultiplier(e.target.value)}
                    placeholder="1.0"
                    className="max-w-32"
                  />
                  <p className="text-xs text-muted-foreground">
                    Current multiplier will be updated to this value
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Changes */}
          {selectedBot && (
            <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <Label className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    Preview Changes
                  </Label>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Backend Bot:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{currentBot.name}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono text-xs">
                        {selectedBot.name} {isReversePosition ? '(Shadow)' : '(Original)'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Display Page:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{currentBot.name}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono text-xs">
                        {keepOnCurrentPage ? currentBot.name : selectedBot.name}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Position Mode:</span>
                    <Badge variant={isReversePosition ? "destructive" : "default"}>
                      {isReversePosition ? 'Reverse' : 'Normal'}
                    </Badge>
                  </div>

                  {selectedBot.id === currentBot.id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Transformation:</span>
                      <Badge variant="outline">
                        {isReversePosition ? 'Switch to Shadow Bot' : 'Keep as Original Bot'}
                      </Badge>
                    </div>
                  )}

                  {updateVolumeMultiplier && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Volume Multiplier:</span>
                      <Badge variant="secondary">
                        {newVolumeMultiplier}x
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleClose}
              disabled={isReassigning}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReassign}
              disabled={isReassigning || !isAdmin}
              className="flex-1"
            >
              {isReassigning ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Reassigning...
                </>
              ) : (
                'Reassign Account'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};